import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { Screen, Skeleton, Stack, Container } from '@fluent/ui';
import { useAuth } from './authStore.js';

/**
 * Gate: renders `children` only if the user is signed in.
 * While auth is still resolving (`authReady=false`), shows a branded
 * skeleton screen. When there's no session, `/` goes to `/welcome` and any
 * other path redirects to `/login`, preserving the attempted path so
 * post-login can bounce back.
 *
 * Also routes first-time users (profile.has_completed_onboarding=false)
 * to /onboarding so they pick a daily goal before the home tree appears.
 * /onboarding itself passes `skipOnboardingGate` so it doesn't redirect
 * to itself.
 */
export function ProtectedRoute({
  children,
  skipOnboardingGate = false,
}: {
  children: ReactNode;
  skipOnboardingGate?: boolean;
}) {
  const { authReady, isAuthenticated, profile, profileLoading } = useAuth();
  const location = useLocation();

  if (!authReady) {
    return <AuthSkeleton />;
  }
  if (!isAuthenticated) {
    // The front door gets the welcome page (what fluent.ge is, the price,
    // the legal links) — a bare sign-in form there is a "login wall" to
    // Paddle's domain review. Deep links go straight to sign-in and back.
    if (location.pathname === '/') {
      return <Navigate to={{ pathname: '/welcome', search: location.search }} replace />;
    }
    // Keep the query too: a Paddle "fix your payment" link is
    // /subscription?_ptxn=… and must still open the card form after sign-in.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  // Spin only while the profile is actively loading. Once the load is
  // done, even if the row is null (rare — auth.users trigger creates it),
  // we proceed: a missing profile is treated as not-onboarded so the user
  // ends up on /onboarding which can recover by upserting.
  if (profileLoading) {
    return <AuthSkeleton />;
  }
  const onboarded = profile?.has_completed_onboarding === true;
  if (!skipOnboardingGate && !onboarded) {
    return <Navigate to="/onboarding" replace />;
  }
  return <>{children}</>;
}

/**
 * Gate: renders `children` only if the user is signed OUT.
 * Used on auth pages (login, signup) so already-signed-in users don't
 * stumble back onto a login form.
 */
export function PublicRoute({ children }: { children: ReactNode }) {
  const { authReady, isAuthenticated } = useAuth();

  if (!authReady) {
    return <AuthSkeleton />;
  }
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

/**
 * AuthSkeleton — branded loading placeholder shown while auth state
 * resolves. Mimics the Home screen layout (top bar + content area) so the
 * transition to real content feels seamless rather than spinner → jump.
 */
export function AuthSkeleton() {
  return (
    <Screen aria-busy="true">
      <Container size="sm">
        <Stack gap={6} style={{ padding: '24px 0' }}>
          {/* Mimic top bar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <Skeleton shape="pill" width={72} height={28} />
              <Skeleton shape="pill" width={56} height={28} />
            </div>
            <Skeleton shape="circle" width={36} height={36} />
          </div>
          {/* Mimic unit banner */}
          <Skeleton shape="card" width="100%" height={120} />
          {/* Mimic lesson nodes */}
          <Stack gap={4} style={{ alignItems: 'center', paddingTop: 16 }}>
            {[0, 1, 2].map((i) => (
              <Stack key={i} gap={2} style={{ alignItems: 'center' }}>
                <Skeleton shape="circle" width={72} height={72} />
                <Skeleton width={80} height={12} />
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Container>
    </Screen>
  );
}
